/**
 * @file volPayApi.js
 * @description 漫画卷购买预览 / 下单 / 购卷充值预览
 * @module features/comic/api/volPayApi
 */

import axios from '@/shared/utils/axios'
import API from '@/config/api'
import { getAuthToken } from '@/shared/utils/auth'

/**
 * @returns {import('axios').AxiosRequestConfig}
 */
function volPayAuthConfig() {
  return {
    headers: {
      'COMIC-WEB-TOKEN': getAuthToken(),
      'Content-Type': 'application/json',
    },
  }
}

/**
 * 购卷入参只传 comicId / volId / bulkType，禁止带金额或余额
 * @param {import('./volPayTypes').VolPayRequestPayload} payload
 * @returns {{ comicId: string, volId: string, bulkType: string }}
 */
function toVolPayBody(payload) {
  return {
    comicId: String(payload.comicId ?? ''),
    volId: String(payload.volId ?? ''),
    bulkType: payload.bulkType || 'NONE',
  }
}

/**
 * @param {object} data
 * @param {string} fallback
 * @returns {never}
 */
function throwVolPayError(data, fallback) {
  throw Object.assign(new Error(data?.msg ?? data?.errmsg ?? fallback), {
    code: data?.code,
  })
}

/**
 * 购卷预览
 * @param {import('./volPayTypes').VolPayRequestPayload} payload
 * @returns {Promise<import('./volPayTypes').VolPayPreviewVo>}
 */
export async function fetchVolPayPreviewApi(payload) {
  const { data } = await axios.post(API.COMIC.VOL_PAY_PREVIEW, toVolPayBody(payload), volPayAuthConfig())
  if (data.code !== 200 && data.code !== 0) {
    throwVolPayError(data, '获取购卷信息失败')
  }
  return data.data
}

/**
 * 购卷下单
 * @param {import('./volPayTypes').VolPayRequestPayload} payload
 * @returns {Promise<import('./volPayTypes').VolPayBuyVo>}
 */
export async function buyVolPayApi(payload) {
  const { data } = await axios.post(API.COMIC.VOL_PAY_BUY, toVolPayBody(payload), volPayAuthConfig())
  if (data.code !== 200 && data.code !== 0) {
    throwVolPayError(data, '购买失败')
  }
  return data.data
}

/**
 * 购卷场景充值预览（带差额与推荐档，禁止与用户中心 package/list 混用）
 * @param {import('./volPayTypes').VolPayRequestPayload} payload
 * @returns {Promise<import('./volPayTypes').VolPayRechargePreviewVo>}
 */
export async function fetchVolPayRechargePreviewApi(payload) {
  const { data } = await axios.post(
    API.COMIC.VOL_PAY_RECHARGE_PREVIEW,
    toVolPayBody(payload),
    volPayAuthConfig()
  )
  if (data.code !== 200 && data.code !== 0) {
    throwVolPayError(data, '获取充值档位失败')
  }
  return data.data
}
